import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Breadcrumbs,
  Link,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as DepartmentIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { departmentService, employeeService } from '../services/api';
import RichTextEditor from './RichTextEditor';
import EmployeeSelector from './EmployeeSelector';
import DepartmentSelector from './DepartmentSelector';
import './DepartmentManagement.css';
import { useConfirm } from './Common/ConfirmDialog';

const DepartmentManagement = () => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    managerId: null,
    parentDepartmentId: null,
    annualBudget: null
  });
  const [isCodeManual, setIsCodeManual] = useState(false);

  // Load departments and employees
  useEffect(() => {
    loadDepartments();
    loadEmployees();
  }, []);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const data = await departmentService.getAll();
      
      console.log('Departments API Response:', data);
      
      if (Array.isArray(data)) {
        setDepartments(data);
      } else {
        console.warn('API returned non-array data:', data);
        setDepartments([]);
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to load departments');
      console.error('Error loading departments:', err);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const data = await employeeService.getAll();
      setEmployees(data);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  // Generate unique department code based on name
  const generateDepartmentCode = (name) => {
    if (!name) return '';

    // Get first 3 letters of each word, max 2 words
    const words = name.trim().split(/\s+/).slice(0, 2);
    let baseCode = words
      .map(word => word.substring(0, 3).toUpperCase())
      .join('');

    // If single word and less than 3 chars, pad or use as is
    if (words.length === 1 && baseCode.length < 3) {
      baseCode = baseCode.padEnd(3, 'X');
    }

    // Limit to 6 characters max
    baseCode = baseCode.substring(0, 6);

    // Check for uniqueness
    let counter = 1;
    let finalCode = baseCode;

    while (departments.some(dept => dept.code === finalCode && dept.id !== editingItem?.id)) {
      finalCode = `${baseCode}${counter}`;
      counter++;
    }

    return finalCode;
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      managerId: null,
      parentDepartmentId: null,
      annualBudget: null
    });
    setIsCodeManual(false);
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      code: item.code || '',
      name: item.name || '',
      description: item.description || '',
      managerId: item.managerId ?? null,
      managerName: item.managerName || '', // Add manager name for display
      parentDepartmentId: item.parentDepartmentId ?? null,
      annualBudget: item.annualBudget ?? null
    });
    setIsCodeManual(true);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ message: 'Are you sure you want to delete this department?', danger: true, confirmLabel: 'Delete' }))) {
      return;
    }

    try {
      await departmentService.delete(id);
      setSuccess('Department deleted successfully');
      loadDepartments();
    } catch (err) {
      setError('Failed to delete department');
      console.error('Error deleting department:', err);
    }
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await departmentService.update(editingItem.id, formData);
        setSuccess('Department updated successfully');
      } else {
        await departmentService.create(formData);
        setSuccess('Department created successfully');
      }
      
      setDialogOpen(false);
      loadDepartments();
    } catch (err) {
      setError('Failed to save department');
      console.error('Error saving department:', err);
    }
  };

  const handleInputChange = (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value
    };

    // Handle manual code editing
    if (field === 'code') {
      setIsCodeManual(true);
    }

    // Auto-generate code when name changes (only if not manually edited)
    if (field === 'name' && !isCodeManual) {
      if (value) {
        newFormData.code = generateDepartmentCode(value);
      }
    }

    setFormData(newFormData);
  };

  // Function to regenerate code manually
  const handleRegenerateCode = () => {
    if (formData.name) {
      const newCode = generateDepartmentCode(formData.name);
      setFormData(prev => ({ ...prev, code: newCode }));
      setIsCodeManual(false);
    }
  };

  // Function to strip HTML and get plain text for display
  const getPlainText = (html) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  return (
    <Box className="department-management-container">
      {/* Header with Breadcrumbs */}
      <Box className="department-header">
        <Breadcrumbs className="breadcrumbs">
          <Link 
            color="inherit" 
            onClick={() => navigate('/settings')}
            className="breadcrumb-link"
          >
            Settings
          </Link>
          <Typography color="text.primary">Departments</Typography>
        </Breadcrumbs>
        
        <Box className="header-content">
          <Box className="title-section">
            <DepartmentIcon className="header-icon" />
            <Box>
              <Typography variant="h4" className="page-title">
                Departments
              </Typography>
              <Typography variant="body1" className="page-subtitle">
                Manage organizational departments and structure
              </Typography>
            </Box>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            className="add-button"
          >
            Add Department
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} className="alert">
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} className="alert">
          {success}
        </Alert>
      )}

      {/* Content */}
      <Card className="content-card">
        <CardContent>
          <TableContainer component={Paper} className="table-container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Manager</TableCell>
                  <TableCell>Parent Dept</TableCell>
                  <TableCell>Employees</TableCell>
                  <TableCell>Budget</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : !Array.isArray(departments) || departments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No departments found
                    </TableCell>
                  </TableRow>
                ) : (
                  departments.map((item) => (
                    <TableRow key={item.id} className="table-row">
                      <TableCell>
                        <Typography variant="body2" className="code-cell">
                          {item.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" className="name-cell">
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip 
                          title={
                            <div 
                              dangerouslySetInnerHTML={{ 
                                __html: item.description || 'No description' 
                              }} 
                              style={{ maxWidth: '400px' }}
                            />
                          } 
                          arrow
                        >
                          <Typography variant="body2" className="description-cell">
                            {getPlainText(item.description)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className="manager-cell">
                          {item.managerName || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className="parent-dept-cell">
                          {item.parentDepartmentName || 'Top Level'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.employeeCount || 0}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className="budget-cell">
                          {item.annualBudget ? `${item.annualBudget.toLocaleString()} BGN` : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(item)}
                          className="action-button edit-button"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(item.id)}
                          className="action-button delete-button"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '70vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          {editingItem ? 'Edit Department' : 'Add New Department'}
        </DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          <Box className="dialog-form">
            {/* Top Row - Code and Name */}
            <Box className="form-row">
              <Box sx={{ flex: 1, marginRight: 2, position: 'relative' }}>
                <TextField
                  label="Department Code"
                  value={formData.code}
                  onChange={(e) => handleInputChange('code', e.target.value)}
                  margin="normal"
                  required
                  placeholder="Auto-generated from name"
                  fullWidth
                  helperText={isCodeManual ? "Manually edited" : "Auto-generated"}
                  InputProps={{
                    style: {
                      fontFamily: 'monospace',
                      backgroundColor: isCodeManual ? 'white' : '#f8f9fa'
                    }
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleRegenerateCode}
                  disabled={!formData.name}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1
                  }}
                  title="Regenerate code from name"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Box>
              <TextField
                label="Department Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                margin="normal"
                required
                placeholder="e.g., Engineering"
                sx={{ flex: 2 }}
              />
            </Box>

            {/* Second Row - Manager and Parent Department */}
            <Box className="form-row">
              <Box sx={{ flex: 1, marginRight: 2 }}>
                <EmployeeSelector
                  key={`manager-${dialogOpen}`}
                  label="Department Manager"
                  value={formData.managerId}
                  onChange={(value) => handleInputChange('managerId', value)}
                  placeholder="Search and select manager..."
                  helperText="Select an employee to be the department manager"
                  preloadedEmployees={employees}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <DepartmentSelector
                  label="Parent Department"
                  value={formData.parentDepartmentId}
                  onChange={(value) => handleInputChange('parentDepartmentId', value)}
                  placeholder="Select parent department..."
                  helperText="Leave empty for top-level department"
                  excludeDepartmentId={editingItem?.id} // Prevent circular reference
                />
              </Box>
            </Box>

            {/* Third Row - Annual Budget */}
            <Box className="form-row">
              <TextField
                label="Annual Budget"
                value={formData.annualBudget || ''}
                onChange={(e) => handleInputChange('annualBudget', e.target.value ? parseFloat(e.target.value) : null)}
                margin="normal"
                type="number"
                placeholder="e.g., 500000"
                sx={{ flex: 1, marginRight: 2 }}
                helperText="Annual budget in BGN"
                InputProps={{
                  startAdornment: <span style={{ marginRight: '8px', color: '#666' }}>BGN</span>
                }}
              />
              <Box sx={{ flex: 1 }} /> {/* Spacer */}
            </Box>

            {/* Description - Full Width */}
            <Box className="description-section">
              <RichTextEditor
                label="Description"
                value={formData.description}
                onChange={(value) => handleInputChange('description', value)}
                placeholder="Department responsibilities and overview..."
                maxLength={1500}
                helperText="Describe the department's role and responsibilities"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DepartmentManagement;
